import './Product.css'
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Carousel from '../components/Carousel'
import ProductCard, { gallery, inr, pricing, stock, STOCK_LABEL } from '../components/ProductCard'
import { HeartIcon } from '../components/Icons'
import { useCart } from '../context/CartContext'
import { useProducts } from '../hooks/useSiteData'
import { imageSrc } from '../services/api'
import { DELIVERY } from '../data/site'

// Turns the admin's video link into something the page can play:
// YouTube / Vimeo -> embedded player, anything else -> a normal <video> file.
function videoSource(raw) {
  const u = String(raw || '').trim()
  if (!u) return null
  let m = u.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/i)
  if (m) return { kind: 'frame', src: `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0&playsinline=1`, poster: `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` }
  m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/i)
  if (m) return { kind: 'frame', src: `https://player.vimeo.com/video/${m[1]}`, poster: '' }
  // Files uploaded to this site: build the address from the /uploads/... part only, exactly like the
  // images, so a wrong host saved in the link (for example http://localhost:5000) can never break playback.
  const up = u.match(/(\/uploads\/[^?#]+)/)
  if (up) return { kind: 'file', src: imageSrc(up[1]), poster: '' }
  return { kind: 'file', src: /^https?:\/\//i.test(u) ? u : imageSrc(u), poster: '' }
}

// Plays an uploaded video file. If the browser cannot decode it (for example an iPhone
// HEVC / .mov file in Chrome), it shows a message with a link instead of a blank player.
function FileVideo({ src, title }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => { setFailed(false) }, [src])
  if (failed) {
    return (
      <div className="pdp__vfail" role="alert">
        <p>This video can't be played in your browser.</p>
        <a href={src} target="_blank" rel="noreferrer">Open video in a new tab</a>
      </div>
    )
  }
  return (
    <video
      key={src}
      src={src}
      controls
      playsInline
      preload="auto"
      aria-label={title}
      onError={() => setFailed(true)}
    />
  )
}

export default function Product() {
  const { id } = useParams()
  const products = useProducts()
  const p = products.find(x => String(x.id) === id)
  const { dispatch, setOpen, wish, toggleWish } = useCart()
  const [model, setModel] = useState('')
  const [pin, setPin] = useState('')
  const [err, setErr] = useState(false)
  const [active, setActive] = useState(0)
  const track = useRef(null)

  // moving to another product (for example from "You may also like") starts fresh
  useEffect(() => { setActive(0); setModel(''); setPin(''); setErr(false) }, [id])

  if (!p) {
    return (
      <div className="pdp">
        {products.length
          ? <p className="empty">Product not found. <Link className="link" to="/shop">Back to shop</Link></p>
          : <p className="empty">Loading…</p>}
      </div>
    )
  }

  const imgs = gallery(p)
  const vid = videoSource(p.video_url)
  // photos first, then the video as the last slide
  const slides = [...imgs.map(src => ({ type: 'img', src })), ...(vid ? [{ type: 'video', ...vid }] : [])]
  const at = Math.min(active, Math.max(slides.length - 1, 0))
  const goTo = i => {
    const el = track.current
    if (!el) return
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollTo({ left: Math.max(0, Math.min(i, slides.length - 1)) * el.clientWidth, behavior: calm ? 'auto' : 'smooth' })
  }
  const onScroll = e => {
    const el = e.currentTarget
    const i = Math.round(el.scrollLeft / (el.clientWidth || 1))
    if (i !== active) setActive(i)
  }
  const onKey = e => {
    if (e.key === 'ArrowRight') { e.preventDefault(); goTo(at + 1) }
    if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(at - 1) }
  }

  const { price, was, pct } = pricing(p)
  const hue = p.hue ?? 210
  const st = stock(p)
  const ok = st === 'in'

  const chosen = model || p.models?.[0] || ''
  const add = go => {
    if (!ok) return
    // the customer is charged the sale price when there is one
    dispatch({ type: 'add', item: { id: chosen ? `${p.id}|${chosen}` : p.id, name: chosen ? `${p.name} (${chosen})` : p.name, price } })
    if (go) setOpen(true)
  }
  const zone = /^\d{6}$/.test(pin) ? (DELIVERY.localPrefixes.some(x => pin.startsWith(x)) ? DELIVERY.local : DELIVERY.india) : null
  const related = products.filter(x => x.category === p.category && x.id !== p.id)

  return (
    <>
      <div className="pdp">
        <div className="pdp__gallery">
          <div className="pdp__img" style={{ background: `linear-gradient(145deg, hsl(${hue} 35% 94%), hsl(${hue} 45% 80%))` }}>
            {slides.length === 0 && <span>{p.category}</span>}
            {slides.length > 0 && (
              <div className="pdp__track" key={p.id} ref={track} onScroll={onScroll} onKeyDown={onKey} tabIndex={0}
                role="group" aria-roledescription="carousel" aria-label={`${p.name} photos${vid ? ' and video' : ''}`}>
                {slides.map((s, i) => (
                  <div key={`${s.src}-${i}`} className={`pdp__slide${s.type === 'video' ? ' pdp__slide--video' : ''}`}
                    role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${slides.length}`}>
                    {s.type === 'img' && <img src={s.src} alt={i === 0 ? p.name : `${p.name}, view ${i + 1}`} loading={i === 0 ? 'eager' : 'lazy'} draggable="false" />}
                    {/* the player only loads while its slide is showing, so swiping away stops the video */}
                    {s.type === 'video' && i === at && (s.kind === 'frame'
                      ? <iframe src={s.src} title={`${p.name} video`} allow="accelerometer; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
                      : <FileVideo src={s.src} title={`${p.name} video`} />)}
                  </div>
                ))}
              </div>
            )}
            {pct > 0 && <em className="pdp__badge">{pct}% OFF</em>}
            {!ok && <em className={`pdp__stock pdp__stock--${st}`}>{STOCK_LABEL[st]}</em>}
            {slides.length > 1 && (
              <>
                <button type="button" className="pdp__arrow pdp__arrow--prev" onClick={() => goTo(at - 1)} disabled={at === 0} aria-label="Previous image">
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
                <button type="button" className="pdp__arrow pdp__arrow--next" onClick={() => goTo(at + 1)} disabled={at === slides.length - 1} aria-label="Next image">
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
                <span className="pdp__count" aria-hidden="true">{at + 1} / {slides.length}</span>
              </>
            )}
          </div>
          {slides.length > 1 && (
            <div className="pdp__thumbs" role="group" aria-label="Product images">
              {slides.map((s, i) => (
                <button key={`${s.src}-${i}`} type="button" className={`pdp__thumb${s.type === 'video' ? ' pdp__thumb--video' : ''}`} aria-pressed={i === at}
                  aria-label={s.type === 'video' ? 'Show product video' : `Show image ${i + 1} of ${slides.length}`} onClick={() => goTo(i)}
                  style={s.type === 'video' && s.poster ? { backgroundImage: `url(${s.poster})` } : undefined}>
                  {s.type === 'img' ? <img src={s.src} alt="" loading="lazy" /> : <span aria-hidden="true">▶</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="pdp__info">
          <p className="pdp__crumb"><Link to="/shop">Shop</Link> / <Link to={`/shop?category=${encodeURIComponent(p.category)}`}>{p.category}</Link></p>
          <h1>{p.name}</h1>
          <p className="pdp__price">
            <span className={was ? 'pdp__now' : undefined}>{inr(price)}</span>
            {was && (
              <>
                <s className="pdp__was">{inr(was)}</s>
                <span className="pdp__save">Save {inr(was - price)} ({pct}% off)</span>
              </>
            )}
          </p>
          {p.desc && <p className="pdp__desc">{p.desc}</p>}
          {p.models && (
            <label>Phone model
              <select value={chosen} onChange={e => setModel(e.target.value)}>
                {p.models.map(m => <option key={m}>{m}</option>)}
              </select>
            </label>
          )}
          <div className="row">
            {ok ? (
              <>
                <button className="btn btn--navy" onClick={() => add(false)}>Add to cart</button>
                <button className="btn btn--cobalt" onClick={() => add(true)}>Buy now</button>
              </>
            ) : (
              <button className="btn btn--navy" disabled>{STOCK_LABEL[st]}</button>
            )}
            <button className="btn" aria-pressed={wish.includes(p.id)} onClick={() => toggleWish(p.id)}><HeartIcon filled={wish.includes(p.id)} size={18} />{wish.includes(p.id) ? 'Saved' : 'Save'}</button>
          </div>
          {!ok && <p className="pdp__note" role="status">{st === 'soon' ? 'This product is coming soon. Save it to your wishlist to find it later.' : 'This product is currently out of stock.'}</p>}
          <div className="pdp__pin">
            <strong>Check delivery</strong>
            <div className="row">
              <input inputMode="numeric" maxLength="6" placeholder="Pincode" value={pin} aria-label="Pincode" onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setErr(false) }} />
              <button className="btn btn--sm" onClick={() => setErr(!zone)}>Check</button>
            </div>
            {zone && <small>{zone.label}: {zone.eta}. {price >= zone.freeAbove ? 'Free delivery.' : `Delivery ${inr(zone.fee)}, free above ${inr(zone.freeAbove)}.`}</small>}
            {err && <small role="alert">Enter a 6-digit pincode.</small>}
          </div>
        </div>
      </div>
      {related.length > 0 && (
        <section className="section">
          <h2 className="section__title">You may also like</h2>
          <Carousel label="related products">{related.map(x => <ProductCard key={x.id} p={x} />)}</Carousel>
        </section>
      )}
    </>
  )
}